import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { handleLogout } from '../../controllers/authController';
import { handleGetRequests } from '../../controllers/requestController';
import { handleGetQuotes, handleAcceptQuote, handleRejectQuote } from '../../controllers/quoteController';
import { handleStartCheckout, handleReleasePayment, handleGetOrders, handleRequestRefund } from '../../controllers/paymentController';
import { getConversations, getMessages, sendMessage as apiSendMessage, requestAdminHelp, startConversation, updateProfile, createReview } from '../../services/api';
import { encodeHTML } from '../../utils/security';
import { msgStamp } from '../../utils/format';
import ImageLightbox from '../../components/ImageLightbox';
import DeleteAccountCard from '../../components/DeleteAccountCard';

const STATUS = {
  pending:   { label: 'Bekliyor',       cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  quoted:    { label: 'Teklif Geldi',   cls: 'bg-blue-50 text-blue-700 border-blue-200'       },
  completed: { label: 'Tamamlandı',     cls: 'bg-green-50 text-green-700 border-green-200'    },
  cancelled: { label: 'İptal',          cls: 'bg-gray-50 text-gray-500 border-gray-200'       },
};

const COND_LABEL = { original: 'Orijinal', aftermarket: 'Muadil', used: 'Çıkma' };
const LEAD_LABEL = { same_day: 'Aynı Gün', '1_2_days': '1-2 Gün', '3_5_days': '3-5 Gün', '1_week': '1 Hafta+' };

const NAV = [
  { key: 'requests', icon: '📋', label: 'Taleplerim'      },
  { key: 'quotes',   icon: '💬', label: 'Gelen Teklifler' },
  { key: 'orders',   icon: '📦', label: 'Siparişlerim'    },
  { key: 'messages', icon: '✉️', label: 'Mesajlar'        },
  { key: 'profile',  icon: '👤', label: 'Profilim'        },
];

const ESCROW_LABEL = {
  held:     { label: 'Ödendi · Teslimat Bekleniyor', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  released: { label: 'Teslim Onaylandı',             cls: 'bg-green-50 text-green-700 border-green-200'  },
  reversed: { label: 'İade Edildi',                  cls: 'bg-gray-100 text-gray-600 border-gray-200'    },
  none:     { label: 'Ödendi',                       cls: 'bg-green-50 text-green-700 border-green-200'  },
};
const REFUND_LABEL = {
  requested: 'İade talebi inceleniyor',
  approved:  'İade onaylandı',
  rejected:  'İade reddedildi',
};

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();

  const [nav,       setNav]       = useState('requests');
  const [pf,        setPf]        = useState({ full_name: '', phone: '', current_password: '', new_password: '' });
  const [pfSaving,  setPfSaving]  = useState(false);
  const [rateModal, setRateModal] = useState(null);  // { supplier, request, name }
  const [lightbox,  setLightbox]  = useState(null);  // enlarged image src
  const [rateForm,  setRateForm]  = useState({ rating: 5, comment: '' });
  const [sideOpen,  setSideOpen]  = useState(false);
  const [requests,  setRequests]  = useState([]);
  const [quotes,    setQuotes]    = useState([]);
  const [convs,     setConvs]     = useState([]);
  const [activeConv,setActiveConv]= useState(null);
  const [messages,  setMessages]  = useState([]);
  const [msgText,   setMsgText]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [feedback,  setFeedback]  = useState('');

  // ── Load requests ──
  const loadRequests = useCallback(async () => {
    setLoading(true);
    const res = await handleGetRequests();
    setLoading(false);
    if (res.success) setRequests(res.data);
    else setError(res.error);
  }, []);

  // ── Load quotes ──
  const loadQuotes = useCallback(async () => {
    setLoading(true);
    const res = await handleGetQuotes();
    setLoading(false);
    if (res.success) setQuotes(res.data);
    else setError(res.error);
  }, []);

  // One card per request with its offers underneath, so prices can be compared:
  // accepted first, then open offers cheapest-first, rejected last.
  const quoteGroups = useMemo(() => {
    const map = new Map();
    for (const q of quotes) {
      if (!map.has(q.request)) {
        const [vehicle, part] = (q.request_info || '').split(' — ');
        map.set(q.request, { id: q.request, vehicle: vehicle || 'Talep', part: part || '', quotes: [] });
      }
      map.get(q.request).quotes.push(q);
    }
    const rank = q => (q.status === 'accepted' ? 0 : q.status === 'sent' ? 1 : 2);
    const groups = [...map.values()];
    for (const g of groups) {
      g.quotes.sort((a, b) => rank(a) - rank(b) || parseFloat(a.price) - parseFloat(b.price));
      const open = g.quotes.some(q => q.status === 'accepted') ? [] : g.quotes.filter(q => q.status === 'sent');
      g.bestId = open.length > 1
        ? open.reduce((a, b) => (parseFloat(b.price) < parseFloat(a.price) ? b : a)).id
        : null;
    }
    return groups;
  }, [quotes]);

  // ── Load conversations ──
  const loadConvs = useCallback(async () => {
    try {
      const res = await getConversations();
      setConvs(res.data);
      if (res.data.length > 0 && !activeConv) setActiveConv(res.data[0]);
    } catch { setError('Mesajlar yüklenemedi.'); }
  }, [activeConv]);

  // ── Load messages for active conversation ──
  const loadMessages = useCallback(async () => {
    if (!activeConv) return;
    try {
      const res = await getMessages(activeConv.id);
      setMessages(res.data);
    } catch {}
  }, [activeConv]);

  useEffect(() => {
    if (nav === 'requests') loadRequests();
    if (nav === 'quotes')   loadQuotes();
    if (nav === 'orders')   loadOrders();
    if (nav === 'messages') loadConvs();
  }, [nav]);

  useEffect(() => {
    loadMessages();
  }, [activeConv]);

  // Seed the profile form from the current user.
  useEffect(() => {
    if (user) setPf(p => ({ ...p, full_name: user.full_name || '', phone: user.phone || '' }));
  }, [user]);

  const saveProfile = async () => {
    setError('');
    if (pf.new_password && !pf.current_password) {
      return setError('Şifre değiştirmek için mevcut şifrenizi girin.');
    }
    setPfSaving(true);
    const payload = { full_name: pf.full_name, phone: pf.phone };
    if (pf.new_password) {
      payload.current_password = pf.current_password;
      payload.new_password     = pf.new_password;
    }
    try {
      const res = await updateProfile(payload);
      updateUser(res.data);
      setPf(p => ({ ...p, current_password: '', new_password: '' }));
      setFeedback('Profiliniz güncellendi. ✓');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      const errs  = err.response?.data?.errors;
      const first = errs ? Object.values(errs)[0] : null;
      setError(Array.isArray(first) ? first[0] : (first || 'Güncelleme başarısız.'));
    } finally {
      setPfSaving(false);
    }
  };

  const sendMsg = async () => {
    const clean = msgText.replace(/<[^>]*>/g, '').trim().slice(0, 1000);
    if (!clean || !activeConv) return;
    try {
      const res = await apiSendMessage(activeConv.id, { text: clean });
      setMessages(p => [...p, res.data]);
      setMsgText('');
    } catch { setError('Mesaj gönderilemedi.'); }
  };

  const startChat = async (requestId, supplierId) => {
    try {
      const res = await startConversation({ request_id: requestId, supplier_id: supplierId });
      setNav('messages');
      await loadConvs();
      setActiveConv(res.data);
    } catch { setError('Konuşma başlatılamadı.'); }
  };

  const askAdminHelp = async () => {
    if (!activeConv) return;
    try {
      await requestAdminHelp({ conversation_id: activeConv.id });
      setFeedback('Admin desteği talebiniz iletildi. En kısa sürede yanıt verilecek. ✓');
      setTimeout(() => setFeedback(''), 4000);
      loadMessages();
    } catch { setError('Destek talebi gönderilemedi.'); }
  };

  const acceptQuote = async (id) => {
    const res = await handleAcceptQuote(id);
    if (res.success) {
      setFeedback('Teklif kabul edildi! ✓');
      loadQuotes();
      setTimeout(() => setFeedback(''), 3000);
    } else setError(res.error);
  };

  const rejectQuote = async (id) => {
    const res = await handleRejectQuote(id);
    if (res.success) {
      loadQuotes();
    } else setError(res.error);
  };

  // Checkout: clicking "Öde" opens a consent modal (mesafeli satış sözleşmesi)
  // before we redirect to iyzico.
  const [checkoutQuote, setCheckoutQuote] = useState(null);
  const [consent, setConsent]   = useState(false);
  const [paying,  setPaying]    = useState(false);
  const confirmAndPay = async () => {
    if (!consent || !checkoutQuote) return;
    setError('');
    setPaying(true);
    const res = await handleStartCheckout(checkoutQuote.id);
    if (res.success && res.data?.paymentPageUrl) {
      // Leave the SPA for iyzico's hosted Checkout Form (card data never hits us).
      window.location.href = res.data.paymentPageUrl;
    } else {
      setError(res.error || 'Ödeme başlatılamadı.');
      setPaying(false);
      setCheckoutQuote(null);
    }
  };

  // Orders
  const [orders, setOrders] = useState([]);
  const loadOrders = useCallback(async () => {
    setLoading(true);
    const res = await handleGetOrders();
    setLoading(false);
    if (res.success) setOrders(res.data);
    else setError(res.error);
  }, []);

  const [releasing, setReleasing] = useState(null);  // payment id being released
  const confirmDelivery = async (paymentId) => {
    if (!window.confirm('Parçayı teslim aldığınızı onaylıyor musunuz? Onayladıktan sonra ödeme tedarikçiye aktarılır.')) return;
    setError('');
    setReleasing(paymentId);
    const res = await handleReleasePayment(paymentId);
    setReleasing(null);
    if (res.success) {
      setFeedback('Teslimat onaylandı, ödeme tedarikçiye aktarıldı. ✓');
      loadOrders();
      setTimeout(() => setFeedback(''), 3000);
    } else setError(res.error);
  };

  // İade (refund) request
  const [refundOrder,  setRefundOrder]  = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refunding,    setRefunding]    = useState(false);
  const submitRefund = async () => {
    if (refundReason.trim().length < 5) { setError('Lütfen iade gerekçesini yazın (en az 5 karakter).'); return; }
    setError('');
    setRefunding(true);
    const res = await handleRequestRefund(refundOrder.id, refundReason.trim());
    setRefunding(false);
    if (res.success) {
      setRefundOrder(null);
      setRefundReason('');
      setFeedback('İade talebiniz alındı. En kısa sürede değerlendirilecektir. ✓');
      loadOrders();
      setTimeout(() => setFeedback(''), 4000);
    } else setError(res.error);
  };


  const submitReview = async () => {
    setError('');
    try {
      await createReview({
        supplier: rateModal.supplier, request: rateModal.request,
        rating: rateForm.rating, comment: rateForm.comment,
      });
      setRateModal(null);
      setRateForm({ rating: 5, comment: '' });
      setFeedback('Değerlendirmeniz kaydedildi. Teşekkürler! ✓');
      loadQuotes();
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Değerlendirme kaydedilemedi.');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">

      {/* Mobile overlay */}
      {sideOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSideOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`w-56 bg-gray-900 flex flex-col fixed top-0 bottom-0 z-40 transition-transform duration-300
        ${sideOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="px-5 py-5 border-b border-white/10">
          <div className="text-white text-lg font-bold cursor-pointer" onClick={() => navigate('/')}>
            Oto<span className="text-blue-400">ParçamıBul</span>
          </div>
          <div className="text-white/30 text-xs mt-1">Müşteri Paneli</div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map(n => (
            <button key={n.key} onClick={() => { setNav(n.key); setSideOpen(false); }}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition border-l-4
                ${nav === n.key
                  ? 'bg-blue-900/30 text-white border-blue-500'
                  : 'text-white/50 border-transparent hover:bg-white/5 hover:text-white'}`}>
              <span>{n.icon}</span>{n.label}
              {n.key === 'quotes' && quotes.filter(q => q.status === 'sent').length > 0 && (
                <span className="ml-auto bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {quotes.filter(q => q.status === 'sent').length}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center">
              {user?.full_name?.[0] || 'M'}
            </div>
            <div className="min-w-0">
              <div className="text-white text-xs font-medium truncate">{user?.full_name || 'Müşteri'}</div>
              <div className="text-white/30 text-xs truncate">{user?.email || ''}</div>
            </div>
          </div>
          <button onClick={() => handleLogout(logout, navigate)}
            className="w-full text-xs border border-white/20 text-white/50 py-1.5 rounded-lg hover:border-white/40 hover:text-white transition">
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="md:ml-56 flex-1 flex flex-col min-w-0">

        {/* Header */}
        <header className="bg-white border-b border-gray-200 h-14 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-gray-500 text-xl" onClick={() => setSideOpen(o => !o)}>☰</button>
            <h1 className="text-base font-semibold text-gray-800">{NAV.find(n => n.key === nav)?.label}</h1>
          </div>
          <button onClick={() => navigate('/request')}
            className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-3 md:px-4 py-2 rounded-lg transition">
            + Yeni Talep
          </button>
        </header>

        <div className="p-4 md:p-6 flex-1">

          {/* Global feedback */}
          {feedback && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
              {feedback}
            </div>
          )}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')} className="ml-4 text-red-400">✕</button>
            </div>
          )}

          {/* ── Requests ── */}
          {nav === 'requests' && (
            <div>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Toplam Talep', val: requests.length },
                  { label: 'Bekleyen',     val: requests.filter(r => r.status === 'pending').length },
                  { label: 'Teklif Geldi', val: requests.filter(r => r.status === 'quoted').length  },
                  { label: 'Tamamlanan',   val: requests.filter(r => r.status === 'completed').length },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{s.label}</div>
                    <div className="text-2xl font-bold text-gray-800">{s.val}</div>
                  </div>
                ))}
              </div>

              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}

              {!loading && requests.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                  <div className="text-4xl mb-3">📋</div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Henüz talebiniz yok</p>
                  <p className="text-xs text-gray-400 mb-4">İlk parça talebinizi oluşturun</p>
                  <button onClick={() => navigate('/request')}
                    className="bg-blue-700 text-white text-sm px-5 py-2 rounded-xl">
                    Talep Oluştur
                  </button>
                </div>
              )}

              {!loading && requests.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  {/* Desktop headers */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <div className="col-span-1">No</div>
                    <div className="col-span-3">Parça</div>
                    <div className="col-span-3">Araç</div>
                    <div className="col-span-2">Tarih</div>
                    <div className="col-span-2">Durum</div>
                    <div className="col-span-1">Teklifler</div>
                  </div>
                  {requests.map(r => (
                    <div key={r.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 border-b border-gray-50 hover:bg-gray-50 transition items-center">
                      <div className="col-span-1 text-xs text-gray-400 font-mono">#{r.id}</div>
                      <div className="col-span-3 text-sm font-semibold text-gray-800">{r.part_name}</div>
                      <div className="col-span-3 text-sm text-gray-500">🚗 {r.brand} {r.model} {r.year}</div>
                      <div className="col-span-2 text-xs text-gray-400">
                        {new Date(r.created_at).toLocaleDateString('tr-TR')}
                      </div>
                      <div className="col-span-2">
                        <span className={`text-xs font-semibold border px-2.5 py-1 rounded-full ${STATUS[r.status]?.cls || STATUS.pending.cls}`}>
                          {STATUS[r.status]?.label || r.status}
                        </span>
                      </div>
                      <div className="col-span-1">
                        {r.quote_count > 0
                          ? <button onClick={() => setNav('quotes')}
                              className="text-xs bg-blue-700 text-white px-2.5 py-1 rounded-lg">
                              {r.quote_count} teklif
                            </button>
                          : <span className="text-xs text-gray-300">—</span>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Quotes ── */}
          {nav === 'quotes' && (
            <div>
              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {!loading && quotes.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                  <div className="text-4xl mb-3">💬</div>
                  <p className="text-sm text-gray-500">Henüz teklif gelmedi.</p>
                </div>
              )}
              <div className="space-y-5">
                {quoteGroups.map(g => (
                  <section key={g.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">🚗 {g.vehicle}</p>
                        {g.part && <p className="text-xs text-gray-500 truncate">{g.part}</p>}
                      </div>
                      <span className="flex-shrink-0 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full">
                        {g.quotes.length} teklif
                      </span>
                    </div>
                    {g.quotes.map(q => (
                      <div key={q.id}
                        className="p-5 flex flex-col md:flex-row items-start md:items-center gap-4 border-t border-gray-100 first:border-t-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-800">{q.supplier_name}</span>
                            {q.supplier_verified && (
                              <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded"
                                title="Bu tedarikçinin vergi levhası OtoParçamıBul tarafından kontrol edildi">
                                ✓ Doğrulanmış satıcı
                              </span>
                            )}
                            {q.supplier_city && <span className="text-xs text-gray-400">📍 {q.supplier_city}</span>}
                            {q.supplier_rating != null && (
                              <span className="text-xs text-amber-600 font-semibold" title={`${q.supplier_review_count} değerlendirme`}>
                                ★ {q.supplier_rating}
                                <span className="text-gray-400 font-normal"> ({q.supplier_review_count})</span>
                              </span>
                            )}
                            <span className={`text-xs border px-2 py-0.5 rounded-full
                              ${q.condition === 'original' ? 'bg-green-50 text-green-700 border-green-200'
                                : q.condition === 'aftermarket' ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                              {COND_LABEL[q.condition] || q.condition}
                            </span>
                            {q.status !== 'sent' && (
                              <span className={`text-xs border px-2 py-0.5 rounded-full
                                ${q.status === 'accepted' ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-red-50 text-red-600 border-red-200'}`}>
                                {q.status === 'accepted' ? '✓ Kabul Edildi' : '✕ Reddedildi'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400">
                            🚚 Temin: {LEAD_LABEL[q.lead_time] || q.lead_time}
                            {q.vat_included ? ' · KDV Dahil' : ' · KDV Hariç'}
                          </p>
                          {q.notes && <p className="text-xs text-gray-400 mt-1 italic">"{q.notes}"</p>}
                          {q.images?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {q.images.map((src, i) => (
                                <button key={i} type="button" onClick={() => setLightbox(src)}>
                                  <img src={src} alt="Teklif fotoğrafı"
                                    className="w-14 h-14 rounded-lg object-cover border border-gray-200 hover:opacity-90 transition" />
                                </button>
                              ))}
                            </div>
                          )}
                          {q.iban && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                              <span className="text-xs text-gray-500">🏦 IBAN:</span>
                              <span className="text-xs font-mono text-gray-700 tracking-wide">{q.iban.replace(/(.{4})/g, '$1 ').trim()}</span>
                              <button type="button" onClick={() => navigator.clipboard?.writeText(q.iban)}
                                className="text-xs text-blue-600 hover:text-blue-800 ml-auto">Kopyala</button>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 md:text-right">
                          {q.id === g.bestId && (
                            <div className="text-[10px] font-extrabold uppercase tracking-wide text-green-600">En uygun</div>
                          )}
                          <div className="text-2xl font-bold text-gray-900">₺{parseFloat(q.price).toLocaleString('tr-TR')}</div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => startChat(q.request, q.supplier)}
                            className="border border-blue-200 text-blue-700 text-xs px-3 py-2 rounded-lg hover:bg-blue-50 transition">
                            💬 Mesaj
                          </button>
                          {q.status === 'sent' && (
                            <>
                              <button onClick={() => rejectQuote(q.id)}
                                className="border border-gray-200 text-gray-500 text-xs px-3 py-2 rounded-lg hover:border-red-300 hover:text-red-600 transition">
                                Reddet
                              </button>
                              <button onClick={() => acceptQuote(q.id)}
                                className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
                                Kabul Et
                              </button>
                            </>
                          )}
                          {q.status === 'accepted' && (
                            <>
                              {!q.paid ? (
                                <button onClick={() => { setCheckoutQuote(q); setConsent(false); }}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
                                  💳 Öde
                                </button>
                              ) : (
                                <button onClick={() => setNav('orders')}
                                  className="text-xs text-green-700 font-semibold bg-green-50 border border-green-200 px-3 py-2 rounded-lg hover:bg-green-100 transition">
                                  ✓ Ödendi · Siparişlerim
                                </button>
                              )}
                              {q.reviewed ? (
                                <span className="text-xs text-amber-600 font-semibold px-3 py-2">★ Değerlendirildi</span>
                              ) : (
                                <button onClick={() => setRateModal({ supplier: q.supplier, request: q.request, name: q.supplier_name })}
                                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
                                  ⭐ Değerlendir
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </div>
          )}

          {/* ── Orders (Siparişlerim) ── */}
          {nav === 'orders' && (
            <div>
              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {!loading && orders.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                  <div className="text-4xl mb-3">📦</div>
                  <p className="text-sm text-gray-500">Henüz siparişiniz yok.</p>
                </div>
              )}
              <div className="space-y-4">
                {orders.map(o => {
                  const esc = ESCROW_LABEL[o.escrow_status] || ESCROW_LABEL.none;
                  const refunded = o.status === 'refunded';
                  return (
                    <div key={o.id}
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-800">{o.supplier_name}</span>
                          <span className="text-xs text-gray-400">Sipariş #{o.id}</span>
                          {refunded ? (
                            <span className="text-xs border px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border-gray-200">İade Edildi</span>
                          ) : (
                            <span className={`text-xs border px-2 py-0.5 rounded-full ${esc.cls}`}>{esc.label}</span>
                          )}
                          {o.refund && (
                            <span className="text-xs border px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border-blue-200">
                              {REFUND_LABEL[o.refund.status] || 'İade'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{o.part_info}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(o.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                      <div className="text-xl font-bold text-gray-900 flex-shrink-0">
                        ₺{parseFloat(o.paid_price || o.price).toLocaleString('tr-TR')}
                      </div>
                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        {!refunded && o.escrow_status === 'held' && (
                          <button onClick={() => confirmDelivery(o.id)} disabled={releasing === o.id}
                            className="bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition"
                            title="Parçayı teslim aldıysanız onaylayın; ödeme tedarikçiye aktarılır.">
                            {releasing === o.id ? 'İşleniyor…' : '📦 Teslim Aldım'}
                          </button>
                        )}
                        {!refunded && !o.refund && (
                          <button onClick={() => { setRefundOrder(o); setRefundReason(''); }}
                            className="border border-gray-200 text-gray-500 text-xs px-3 py-2 rounded-lg hover:border-red-300 hover:text-red-600 transition">
                            ↩️ İade Talep Et
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Messages ── */}
          {nav === 'messages' && (
            <div className="flex flex-col md:flex-row gap-4 h-[70vh]">
              {/* Conversation list */}
              <div className="w-full md:w-56 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-y-auto flex-shrink-0">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Konuşmalar</p>
                </div>
                {convs.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-6">Henüz mesajınız yok</p>
                )}
                {convs.map(c => (
                  <button key={c.id} onClick={() => setActiveConv(c)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition
                      ${activeConv?.id === c.id ? 'bg-blue-50' : ''}`}>
                    <p className="text-xs font-semibold text-gray-800 truncate">
                      {c.other_party?.name || 'Tedarikçi'}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{c.request_summary}</p>
                    {c.unread_count > 0 && (
                      <span className="mt-1 inline-block bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {c.unread_count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Chat window */}
              <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
                {!activeConv ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                    Bir konuşma seçin
                  </div>
                ) : (
                  <>
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {activeConv.other_party?.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{activeConv.request_summary}</p>
                      </div>
                      <button onClick={askAdminHelp} title="Sorun mu var? Admin desteği isteyin"
                        className="flex-shrink-0 text-xs bg-red-50 text-red-700 border border-red-200 font-semibold px-3 py-1.5 rounded-lg hover:bg-red-100 transition">
                        🆘 Admin Desteği
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 space-y-3">
                      {messages.map(m => (
                        <div key={m.id} className={`flex ${m.is_mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs md:max-w-sm px-4 py-2.5 rounded-2xl text-sm
                            ${m.is_mine
                              ? 'bg-blue-700 text-white rounded-br-none'
                              : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                            <p dangerouslySetInnerHTML={{ __html: encodeHTML(m.text) }} />
                            <p className={`text-xs mt-1 ${m.is_mine ? 'text-blue-200' : 'text-gray-400'}`}>
                              {msgStamp(m.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-100 flex gap-3">
                      <input type="text"
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-600 transition"
                        placeholder="Mesajınızı yazın..."
                        value={msgText}
                        maxLength={1000}
                        onChange={e => setMsgText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMsg()}
                      />
                      <button onClick={sendMsg}
                        className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition">
                        Gönder
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Profile ── */}
          {nav === 'profile' && (
            <div className="max-w-lg space-y-5">
              {/* Editable profile */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7">
                <h3 className="text-base font-bold text-gray-800 mb-5">Profil Bilgileri</h3>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Ad Soyad</label>
                <input type="text" maxLength={100}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.full_name}
                  onChange={e => setPf(p => ({ ...p, full_name: e.target.value }))} />

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Telefon</label>
                <input type="tel" maxLength={15} placeholder="05xx xxx xx xx"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.phone}
                  onChange={e => setPf(p => ({ ...p, phone: e.target.value }))} />

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">E-posta</label>
                <input type="email" disabled value={user?.email || ''}
                  className="w-full px-3 py-2.5 border border-gray-100 bg-gray-50 text-gray-400 rounded-lg text-sm mb-1 cursor-not-allowed" />
                <p className="text-xs text-gray-400 mb-2">E-posta adresi değiştirilemez.</p>

                <div className="text-xs text-gray-400 pt-2">
                  Katılım: {user?.date_joined ? new Date(user.date_joined).toLocaleDateString('tr-TR') : '—'}
                </div>
              </div>

              {/* Password change */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7">
                <h3 className="text-base font-bold text-gray-800 mb-1">Şifre Değiştir</h3>
                <p className="text-xs text-gray-400 mb-5">Değiştirmek istemiyorsanız boş bırakın.</p>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mevcut Şifre</label>
                <input type="password" maxLength={128} autoComplete="current-password"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.current_password}
                  onChange={e => setPf(p => ({ ...p, current_password: e.target.value }))} />

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Yeni Şifre</label>
                <input type="password" maxLength={128} autoComplete="new-password" placeholder="Min. 8 karakter, 1 büyük harf, 1 rakam"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.new_password}
                  onChange={e => setPf(p => ({ ...p, new_password: e.target.value }))} />
              </div>

              <button onClick={saveProfile} disabled={pfSaving}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition">
                {pfSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>

              <DeleteAccountCard />
            </div>
          )}

        </div>
      </main>

      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />

      {/* ── Checkout consent modal (mesafeli satış sözleşmesi) ── */}
      {checkoutQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => !paying && setCheckoutQuote(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-7 relative"
            onClick={e => e.stopPropagation()}>
            <button onClick={() => !paying && setCheckoutQuote(null)}
              className="absolute top-4 right-4 text-gray-300 hover:text-gray-500 text-xl leading-none">✕</button>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Siparişi Onayla ve Öde</h3>

            {/* Order summary */}
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 mb-4 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-400">Tedarikçi</span><span className="font-medium text-gray-800">{checkoutQuote.supplier_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Parça</span><span className="font-medium text-gray-800 text-right ml-4">{checkoutQuote.request_info}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Temin</span><span className="text-gray-700">{LEAD_LABEL[checkoutQuote.lead_time] || checkoutQuote.lead_time}</span></div>
              <div className="flex justify-between border-t border-gray-200 pt-1.5 mt-1.5">
                <span className="text-gray-500 font-semibold">Toplam</span>
                <span className="font-bold text-gray-900">₺{parseFloat(checkoutQuote.price).toLocaleString('tr-TR')} {checkoutQuote.vat_included ? '(KDV Dahil)' : '(KDV Hariç)'}</span>
              </div>
            </div>

            <label className="flex items-start gap-2.5 mb-5 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0" />
              <span className="text-xs text-gray-600 leading-relaxed">
                <button type="button" onClick={() => window.open('/yasal/on-bilgilendirme', '_blank')} className="text-blue-700 underline">Ön Bilgilendirme Formu</button>'nu ve{' '}
                <button type="button" onClick={() => window.open('/yasal/mesafeli-satis-sozlesmesi', '_blank')} className="text-blue-700 underline">Mesafeli Satış Sözleşmesi</button>'ni okudum, onaylıyorum.
              </span>
            </label>

            <p className="text-xs text-gray-400 mb-4">
              🔒 Ödeme iyzico ile güvenli şekilde alınır ve parçayı teslim aldığınızı onaylayana kadar satıcıya aktarılmaz. Kart bilgileriniz bizde saklanmaz.
            </p>

            <button onClick={confirmAndPay} disabled={!consent || paying}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl text-sm transition">
              {paying ? 'Yönlendiriliyor…' : 'Onayla ve Öde'}
            </button>
          </div>
        </div>
      )}

      {/* ── Refund (iade) request modal ── */}
      {refundOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => !refunding && setRefundOrder(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-7 relative"
            onClick={e => e.stopPropagation()}>
            <button onClick={() => !refunding && setRefundOrder(null)}
              className="absolute top-4 right-4 text-gray-300 hover:text-gray-500 text-xl leading-none">✕</button>
            <h3 className="text-lg font-bold text-gray-800 mb-1">İade Talebi</h3>
            <p className="text-sm text-gray-400 mb-4">{refundOrder.part_info}</p>

            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">İade Gerekçesi</label>
            <textarea rows={4} maxLength={1000}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
              placeholder="Örn. yanlış / hatalı / eksik parça geldi..."
              value={refundReason}
              onChange={e => setRefundReason(e.target.value)} />
            <p className="text-xs text-gray-400 mb-5">
              Talebiniz tedarikçiye ve ekibimize iletilir; en kısa sürede değerlendirilir.
            </p>

            <button onClick={submitRefund} disabled={refunding}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold py-3 rounded-xl text-sm transition">
              {refunding ? 'Gönderiliyor…' : 'İade Talebini Gönder'}
            </button>
          </div>
        </div>
      )}

      {/* ── Rate-supplier modal ── */}
      {rateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => setRateModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-7 relative"
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setRateModal(null)}
              className="absolute top-4 right-4 text-gray-300 hover:text-gray-500 text-xl leading-none">✕</button>
            <h3 className="text-lg font-bold text-gray-800 mb-1">Tedarikçiyi Değerlendir</h3>
            <p className="text-sm text-gray-400 mb-5">{rateModal.name}</p>

            <div className="flex justify-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setRateForm(f => ({ ...f, rating: n }))}
                  className={`text-3xl transition ${n <= rateForm.rating ? 'text-amber-400' : 'text-gray-200'}`}>
                  ★
                </button>
              ))}
            </div>

            <textarea rows={3} maxLength={500}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
              placeholder="Yorumunuz (isteğe bağlı)"
              value={rateForm.comment}
              onChange={e => setRateForm(f => ({ ...f, comment: e.target.value }))} />

            <button onClick={submitReview}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-xl text-sm transition">
              Değerlendirmeyi Gönder
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
